// models/commerce/PoolaOffer.js
import { Model } from 'pinia-orm';
import { POOLA_NEAR_COMPLETE_RATIO } from '@/constants/poola.js';

/**
 * PoolaOffer — a claimable offer card on the member Poola buy surface
 * (GET /api/v1/poola-offers?filter[deal]=<runId>). Mirrors the wire shape of
 * the backend PoolaOfferResource exactly (camelCase attributes, flattened onto
 * `data` by the JSON:API deserialiser). Read the real contract — do not guess:
 *   ../backend/app/JsonApi/V1/PoolaOffers/PoolaOfferResource.php
 *   ../backend/app/JsonApi/V1/PoolaOffers/PoolaOfferSchema.php
 *   ../backend/_bmad-output/planning-artifacts/poola-pwa-fe-contract-2026-06-30.md Section 3
 *
 * Storing offers in this pinia-orm repo gives the cursor list a stable
 * dedupe-by-id across pages and a clean LOCAL patch after a server-confirmed
 * claim (decrement piecesRemaining, bump myClaimedQty + piecesClaimed) — see
 * usePoolaOffers.js.
 *
 * MONEY: every *Cents field is integer EGP cents (1 PoolShare cent = 1 EGP
 * cent); per unit `cashCents + poolshareCents = unitPriceCents`. Pass cents
 * straight to useCurrency().formatCurrency() — never hand-format.
 *
 * `id` is the offer id; it is also the {offer} path param for the claim POST.
 *
 *   - displayTitle       string       resolved card headline (offer › variant › product)
 *   - photoUrl           string|null  offer/product image (space disk)
 *   - unitPriceCents     int          full retail per unit (cents)
 *   - discountBp         int          discount in basis points (= the PoolShare share; 3000 = 30%)
 *   - cashCents          int          per-unit cash paid from the default wallet
 *   - poolshareCents     int          per-unit PoolShare burned (final, never refundable)
 *   - piecesTotal        int          total pieces in the offer
 *   - piecesRemaining    int          live stock (piecesTotal − piecesClaimed)
 *   - piecesClaimed      int          raw claimed count (progress bar + social proof)
 *   - maxPerMember       int|null     EFFECTIVE per-member piece cap in force NOW (null = uncapped).
 *                                     Once a scheduled cap-increase activates this is base+delta,
 *                                     permanently for the rest of the deal — treat as live, not fixed.
 *   - upcomingMaxPerMember int|null   effective cap AFTER the scheduled bump activates; null = uncapped.
 *                                     Equals maxPerMember when no bump is scheduled. Compare against
 *                                     maxPerMember to detect a pending lift (see the cap-unlock countdown
 *                                     recipe in ../backend/_bmad-output/sync/briefs/2026-07-13-poola-cap-increase.md).
 *   - status             int          1=ACTIVE, 2=SOLD_OUT (POOLA_OFFER_STATUS)
 *   - dealId             string       parent run id (= the filter[deal] value)
 *   - myClaimedQty       int          pieces this member currently holds (PURCHASED only)
 *   - canClaim           bool         advisory "can claim ≥1 right now" (server is the real gate)
 *   - productName        string|null  catalog product name (null for STANDALONE/title-only)
 *   - variantName        string|null  catalog variant name (null when no variant)
 *   - supplierName       string|null  supplier display name (null when unresolved → STANDALONE)
 *   - supplierAvatarUrl  string|null  supplier avatar (space disk)
 *   - supplierIsPlatform bool         platform-vetted supplier → show the "موثّق" badge
 *   - postContent        string|null  SANITIZED supplier post text — flavor only, NEVER parse for contact
 *   - telegramPostUrl    string|null  public deep-link; hide the link entirely when null
 *   - postId             string|null  linked Posts-repo post id (null for STANDALONE). Flat attr
 *                                     stamped by usePoolaOffers after persisting the included post
 *                                     into the Posts repo — NOT a relation on this model.
 *                                     Use hasShareablePost to gate the share affordance.
 *   - myPoolcreditEarnedCents int|null this member's poolcredit reward for a COMPLETED offer
 *                                     (see isComplete). null when not granted (ACTIVE / standalone
 *                                     / non-claimant) — feature-detect, never render a placeholder.
 *                                     DELIVERED as a derived per-viewer resource field
 *                                     (../backend/_bmad-output/sync/briefs/
 *                                     2026-07-13-poola-completion-poolcredit-DELIVERED.md); patched
 *                                     live on the completion frame by usePoolaOffers.applyClaimBroadcast
 *                                     (P6-A) so it lights up at the sold-out instant, not only on refetch.
 */
export default class PoolaOffer extends Model {
  static entity = 'poola-offers';

  static fields() {
    return {
      id: this.uid(),
      displayTitle: this.attr(null),
      photoUrl: this.attr(null),
      unitPriceCents: this.number(0),
      discountBp: this.number(0),
      cashCents: this.number(0),
      poolshareCents: this.number(0),
      piecesTotal: this.number(0),
      piecesRemaining: this.number(0),
      piecesClaimed: this.number(0),
      // attr(null), NOT number(0): null means "uncapped" and must survive — a
      // numeric default would coerce it to 0 ("no pieces allowed"), inverting
      // the meaning.
      maxPerMember: this.attr(null),
      // attr(null), NOT number(0): same "null = uncapped" contract as maxPerMember
      // (a bump never imposes a cap on an uncapped offer). Effective cap AFTER the
      // scheduled cap-increase; equals maxPerMember when no bump is scheduled.
      upcomingMaxPerMember: this.attr(null),
      status: this.number(0),
      dealId: this.attr(null),
      myClaimedQty: this.number(0),
      canClaim: this.boolean(false),
      productName: this.attr(null),
      variantName: this.attr(null),
      supplierName: this.attr(null),
      supplierAvatarUrl: this.attr(null),
      supplierIsPlatform: this.boolean(false),
      postContent: this.attr(null),
      telegramPostUrl: this.attr(null),
      // Flat id pointing at the Posts repo — NOT a relation on PoolaOffer.
      // The post lives in the Posts pinia-orm repo; PlatformShare resolves it
      // via useRepo(Post).with('media').find(postId). Stamped by usePoolaOffers
      // after postRepo.save(includedPosts) per page.
      postId: this.attr(null),
      // Supplier read-only path (authoritative on the full read only; mirrors canClaim's sparse caveat).
      isMine: this.boolean(false),
      supplierId: this.attr(null),
      myPoolcreditEarnedCents: this.attr(null),
    };
  }

  // True when this offer has a linked post that PlatformShare can share.
  // Gate the share affordance on this + the run phase (hide when ended).
  get hasShareablePost() {
    return this.postId != null;
  }

  // The offer completed — every piece is claimed. Equivalent to
  // status === POOLA_OFFER_STATUS.SOLD_OUT; expressed via piecesRemaining to
  // stay correct even if a caller hasn't patched `status` yet on the same tick.
  // Guards:
  //   - piecesTotal > 0 so a malformed 0-piece offer (or a row where the wire
  //     omitted piecesRemaining → number(0) default) is NOT vacuously "complete"
  //     and mis-celebrated as sold out (it never had stock).
  //   - `<= 0`, not `=== 0`, so a server-sent oversell (negative remaining) still
  //     reads as complete rather than slipping back to the live/near-complete band.
  get isComplete() {
    return this.piecesTotal > 0 && this.piecesRemaining <= 0;
  }

  // Advisory "about to complete" signal for the urgency copy/skin — never true
  // once the offer is actually complete. Either the claimed ratio crosses the
  // near-complete threshold, or the low-stock floor is hit (same floor used
  // elsewhere for the "last N pieces" tag). piecesTotal <= 0 is a guard against
  // a divide-by-zero on a malformed/empty offer.
  get isNearComplete() {
    if (this.isComplete) return false;
    if (!(this.piecesTotal > 0)) return false;
    const ratio = this.piecesClaimed / this.piecesTotal;
    const lowStockFloor = Math.max(5, Math.floor(this.piecesTotal * 0.1));
    return ratio >= POOLA_NEAR_COMPLETE_RATIO || this.piecesRemaining <= lowStockFloor;
  }
}
